/* eslint-disable no-underscore-dangle */
import cache from '../../utils/cache';
import ScreensService from '../../services/screens';

const state = {
  isPreview: false,
  connected: false,
  screenId: null,
  productionId: null,
  isReady: false,
  previewRoom: null,
  verificationKey: null,
  displayToken: null,
  isGenerated: false,
  screen: null,
};

const updateMeta = (name, value) => {
  let el = document.querySelector(`meta[name=data-${name}]`);

  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', `data-${name}`);
    document.head.appendChild(el);
  }

  el.setAttribute('content', value);
};

const actions = {
  /**
   * Initialize current display using either productionId or display hash.
   */
  async initDisplay(context, route) {
    const {
      displayHash,
      productionId,
    } = route.params;

    const { name: routeName } = route;

    if (productionId) {
      const {
        verification_key: verificationKey,
      } = route.query;

      context.commit('SET_PRODUCTION_ID', productionId);

      if (routeName === 'production-preview') {
        context.commit('SET_PREVIEW_ROOM', `${productionId}_preview`);
      }

      context.commit('SET_VERIFICATION_KEY', verificationKey);
    } else if (displayHash) {
      const parts = Buffer.from(displayHash, 'base64').toString().split('.');
      parts[0] = Buffer.from(parts[0], 'base64').toString();

      const [displayToken, screenId, verificationKey] = parts;

      context.commit('SET_SCREEN_ID', screenId);
      context.commit('SET_VERIFICATION_KEY', verificationKey);
      context.commit('SET_DISPLAY_TOKEN', displayToken);
      context.commit('SET_IS_GENERATED', true); // All hash routes are considered "generated" outputs.

      await context.dispatch('getScreen', screenId);
    }

    // Should be safe to connect to socket server now that meta properties exist.
    const interval = setInterval(() => {
      if (window.$socket?.connect) {
        window.$socket.connect();
        clearInterval(interval);
      }
    }, 100);
  },

  async getScreen(context, id) {
    if (!id) {
      throw new Error('Missing id');
    }

    const screen = await ScreensService.getScreen(id);
    context.commit('SET_SCREEN', screen ?? null);

    if (screen?.production_id) {
      context.commit('SET_PRODUCTION_ID', screen.production_id);
    }

    return screen ?? null;
  },

  setIsReady(context, payload) {
    context.commit('SET_IS_READY', payload);
  },

  setScreenId(context, payload) {
    context.commit('SET_SCREEN_ID', payload);
  },

  setIsPreview(context, payload) {
    context.commit('SET_IS_PREVIEW', payload);
  },

  setConnected(context, payload) {
    context.commit('SET_CONNECTED', payload);
  },

  setProductionId(context, id) {
    context.commit('SET_PRODUCTION_ID', id);
  },

  setCache(context, payload) {
    context.commit('SET_CACHE', payload);
  },

  socket_joinProduction(context, payload) {
    window.$socket.emit('production', payload);
    context.commit('SET_CACHE', payload);
    context.commit('SCREEN_CONNECTED', payload);
  },

  socket_leaveProduction(context, productionId) {
    if (productionId) {
      window.$socket.emit('leave', {
        production_id: productionId,
      });
    }

    context.commit('SET_PRODUCTION_ID', null);
  },

  resetScreen(context) {
    context.commit('RESET_SCREEN');
  },

  socket_resetScreen(context) {
    context.commit('RESET_SCREEN');
  },

  setPreviewRoom(context, payload) {
    context.commit('SET_PREVIEW_ROOM', payload);
  },

  setDisplayToken(context, token) {
    context.commit('SET_DISPLAY_TOKEN', token);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SCREEN_CONNECTED(state, payload) {
    state.connected = true;
    state.screenId = payload.screen_id;
    state.productionId = payload.production_id;

    // Cache, retaining existing details if necessary
    const cachedScreen = cache('tgb_producer_screen') || {};
    cache('tgb_producer_screen', {
      ...cachedScreen,
      ...payload,
    });
  },

  SET_PRODUCTION_ID(state, productionId) {
    state.productionId = productionId;
    state.connected = true;
    updateMeta('production-id', productionId);
  },

  SET_SCREEN_ID(state, screenId) {
    state.screenId = screenId;
    updateMeta('screen-id', screenId);
  },

  SET_SCREEN(state, screen) {
    state.screen = screen;
  },

  SET_CONNECTED(state, payload) {
    state.connected = payload;
  },

  SET_IS_PREVIEW(state, isPreview) {
    state.isPreview = isPreview;
    state.connected = true;
  },

  SET_IS_READY(state, payload) {
    state.isReady = payload;
  },

  RESET_SCREEN(state) {
    state.connected = false;
    cache('tgb_producer_screen', null);
    window.location.reload();
  },

  SET_CACHE(state, payload) {
    cache('tgb_producer_screen', payload);
  },

  SET_PREVIEW_ROOM(state, payload) {
    state.previewRoom = payload;
    updateMeta('preview-room', payload);
  },

  SET_VERIFICATION_KEY(state, key) {
    state.verificationKey = key;
    updateMeta('verification-key', key);
  },

  SET_DISPLAY_TOKEN(state, token) {
    state.displayToken = token;
    updateMeta('display-token', token);
  },

  SET_IS_GENERATED(state, isGenerated) {
    state.isGenerated = isGenerated;
    updateMeta('is-generated', isGenerated);
  },
};

const getters = {
  isKeyScreen(state) {
    return state.screenId === state.key_screen;
  },

  getKeyScreen(state) {
    return state.key_screen;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
